import { useRef, useState } from 'react';

/**
 * PhotoUpload — circular avatar preview with file picker + delete.
 *
 * Wraps the photo upload UX for staff + tenant logo. Handles:
 *  - File picker via hidden <input type="file">
 *  - Client-side validation (size, type) before sending
 *  - Visible error + retry path
 *  - Loading state during upload
 *  - Optional initials fallback when no photo exists
 *
 * Props:
 *   photoUrl    — current photo URL or null
 *   initials    — fallback shown when no photo (e.g. "DM" for Dani Morgan)
 *   disabled    — disables interaction (e.g. when parent is saving)
 *   onUpload    — async (file) => Promise<{ photo_url }>
 *   onDelete    — async () => Promise<void>
 *
 * Errors from onUpload/onDelete propagate up — this component just
 * displays a friendly message if provided via props.error.
 */

const MAX_SIZE_MB = 5;
const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function PhotoUpload({
  photoUrl,
  initials,
  disabled = false,
  onUpload,
  onDelete,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    // Always reset the input so picking the same file twice still fires onChange
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    setError(null);

    if (!ACCEPTED_MIME.includes(file.type)) {
      setError('Please choose a JPG, PNG, WebP or GIF file.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File is too large — maximum ${MAX_SIZE_MB}MB.`);
      return;
    }

    setUploading(true);
    try {
      await onUpload?.(file);
    } catch (err) {
      setError(err?.message || 'Could not upload the photo. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    setError(null);
    setDeleting(true);
    try {
      await onDelete?.();
    } catch (err) {
      setError(err?.message || 'Could not remove the photo. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div className="photo-uploader">
        <div className="photo-uploader-preview">
          {photoUrl ? (
            <img src={photoUrl} alt="Staff photo" />
          ) : (
            <span className="staff-avatar-placeholder" style={{ width: '100%', height: '100%' }}>
              {initials || '—'}
            </span>
          )}
        </div>

        <div className="photo-uploader-controls">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_MIME.join(',')}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={disabled || uploading || deleting}
          />

          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 'var(--fs-sm)', padding: '0.45rem 0.9rem' }}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading || deleting}
              aria-busy={uploading}
            >
              {uploading ? (
                <><span className="spinner" aria-hidden="true" /> Uploading…</>
              ) : photoUrl ? (
                'Replace photo'
              ) : (
                'Upload photo'
              )}
            </button>

            {photoUrl && onDelete && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 'var(--fs-sm)', padding: '0.45rem 0.9rem', color: 'var(--id-red-d)' }}
                onClick={handleDelete}
                disabled={disabled || uploading || deleting}
                aria-busy={deleting}
              >
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            )}
          </div>

          <p className="photo-uploader-hint">JPG, PNG, WebP, or GIF — up to {MAX_SIZE_MB}MB.</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 'var(--sp-3)' }} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
